import { Info, AlertTriangle, ExternalLink } from "lucide-react";

const GMC_GUIDANCE_URL =
  "https://www.gmc-uk.org/registration-and-licensing/join-our-registers/registration-applications/portfolio-pathway";

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

function parseDateStr(str) {
  return new Date(str + "T00:00:00");
}

function yearLabel(d) {
  return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

function getItemColor(yearsRemaining) {
  if (yearsRemaining < 0)  return { dot: "#ef4444", bar: "rgba(239,68,68,.8)", label: "Outside window" };
  if (yearsRemaining < 1)  return { dot: "#ef4444", bar: "rgba(239,68,68,.8)", label: "<1yr left" };
  if (yearsRemaining < 2)  return { dot: "#f59e0b", bar: "rgba(245,158,11,.8)", label: "1–2yr left" };
  return                         { dot: "#10b981", bar: "rgba(16,185,129,.8)", label: ">2yr left" };
}

export default function EvidenceCurrencyTimeline({
  deadlines,
  specialtyData,
  themeMode,
  t,
  onNavToItem,
}) {
  const isLight = themeMode === "clinical_light";

  // Only consider active deadlines with an evidence_date
  const itemsWithDate = (deadlines || []).filter(
    d => d.evidence_date && d.status === "active"
  );

  if (itemsWithDate.length === 0) return null;

  const today = new Date();
  const fiveYearsAgo = new Date(today);
  fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
  const totalMs = today - fiveYearsAgo;

  // Compute position and colour for each item
  const items = itemsWithDate.map(d => {
    const date = parseDateStr(d.evidence_date);
    const position = Math.max(-0.05, (date - fiveYearsAgo) / totalMs); // allow slight overflow left
    const ageYears = (today - date) / MS_PER_YEAR;
    const yearsRemaining = 5 - ageYears;
    const colors = getItemColor(yearsRemaining);
    return { ...d, position, ageYears, yearsRemaining, colors };
  });

  const outsideItems = items.filter(i => i.yearsRemaining < 0);
  const approachingItems = items.filter(i => i.yearsRemaining >= 0 && i.yearsRemaining < 2);
  const safeItems = items.filter(i => i.yearsRemaining >= 2);

  // Domain summary — group by domain_id
  const domains = (specialtyData?.domains || []);
  const domainSummary = domains.map(domain => {
    const di = items.filter(i => i.domain_id === domain.id);
    if (di.length === 0) return null;
    return {
      domain,
      inWindow:   di.filter(i => i.yearsRemaining >= 2).length,
      approaching: di.filter(i => i.yearsRemaining >= 0 && i.yearsRemaining < 2).length,
      outside:    di.filter(i => i.yearsRemaining < 0).length,
      total: di.length,
    };
  }).filter(Boolean);

  // Year tick marks along the timeline
  const ticks = [];
  for (let yr = 0; yr <= 5; yr++) {
    const d = new Date(fiveYearsAgo);
    d.setFullYear(d.getFullYear() + yr);
    ticks.push({ pct: (yr / 5) * 100, label: yearLabel(d) });
  }

  const boxStyle = {
    background: t.cardBg,
    border: `1px solid ${outsideItems.length > 0 ? "rgba(239,68,68,.35)" : t.cardBrd}`,
    borderRadius: 12,
    padding: "16px 18px",
    marginBottom: 20,
  };

  return (
    <div style={boxStyle}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: t.headText }}>GMC 5-year evidence window</span>
            <a href={GMC_GUIDANCE_URL} target="_blank" rel="noopener noreferrer" title="GMC Portfolio Pathway guidance" style={{ color: t.muted, display: "flex", alignItems: "center" }}>
              <ExternalLink size={11} />
            </a>
          </div>
          <p style={{ fontSize: 11.5, color: t.bodyText, lineHeight: 1.5, maxWidth: 560 }}>
            The GMC requires primary clinical evidence from within your last 5 years of practice.
            Evidence approaching this limit needs to be supplemented with recent primary evidence or refreshed.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flexShrink: 0 }}>
          <Legend color="#10b981" label={`${safeItems.length} in window`} />
          <Legend color="#f59e0b" label={`${approachingItems.length} approaching`} />
          <Legend color="#ef4444" label={`${outsideItems.length} outside`} />
        </div>
      </div>

      {/* Warning banner */}
      {outsideItems.length > 0 && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 12px", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 8, marginBottom: 14 }}>
          <AlertTriangle size={14} style={{ color: "#ef4444", flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: "#fca5a5", lineHeight: 1.5 }}>
            <strong>{outsideItems.length} clinical evidence item{outsideItems.length !== 1 ? "s are" : " is"} outside the GMC 5-year window.</strong>{" "}
            These must be supported by recent primary evidence or refreshed before submission.{" "}
            <a href={GMC_GUIDANCE_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#f87171" }}>
              GMC guidance →
            </a>
          </div>
        </div>
      )}

      {/* Timeline bar */}
      <div style={{ marginBottom: 18 }}>
        {/* Axis labels */}
        <div style={{ position: "relative", height: 16, marginBottom: 4 }}>
          {ticks.map(tick => (
            <span key={tick.pct} style={{ position: "absolute", left: `${tick.pct}%`, transform: "translateX(-50%)", fontSize: 9, color: t.muted, whiteSpace: "nowrap" }}>
              {tick.label}
            </span>
          ))}
        </div>

        {/* Track */}
        <div style={{ position: "relative", height: 8, background: isLight ? "#E2E8F0" : "#1b3355", borderRadius: 4, marginBottom: 8 }}>
          {/* Coloured fill segments */}
          <div style={{ position: "absolute", left: 0, top: 0, width: "40%", height: "100%", background: "rgba(239,68,68,.25)", borderRadius: "4px 0 0 4px" }} />
          <div style={{ position: "absolute", left: "40%", top: 0, width: "20%", height: "100%", background: "rgba(245,158,11,.2)" }} />
          <div style={{ position: "absolute", left: "60%", top: 0, width: "40%", height: "100%", background: "rgba(16,185,129,.18)", borderRadius: "0 4px 4px 0" }} />
          {/* "Today" marker */}
          <div style={{ position: "absolute", right: 0, top: -4, width: 2, height: 16, background: t.headText, borderRadius: 1 }} />
          {/* "5yr ago" boundary marker */}
          <div style={{ position: "absolute", left: 0, top: -4, width: 2, height: 16, background: "#ef4444", borderRadius: 1 }} />
        </div>

        {/* Dots */}
        <div style={{ position: "relative", height: 28 }}>
          {items.map((item, idx) => {
            const left = Math.max(0, Math.min(100, item.position * 100));
            return (
              <button
                key={item.id || idx}
                onClick={() => onNavToItem?.(item.domain_id)}
                title={`${item.item_label}\nEvidence date: ${item.evidence_date}\n${item.yearsRemaining > 0 ? `${item.yearsRemaining.toFixed(1)} years remaining` : `${Math.abs(item.yearsRemaining).toFixed(1)} years outside window`}`}
                style={{
                  position: "absolute",
                  left: `${left}%`,
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: item.colors.dot,
                  border: `2px solid ${isLight ? "#fff" : "#1b3355"}`,
                  cursor: "pointer",
                  boxShadow: `0 0 0 1px ${item.colors.dot}40`,
                  transition: "transform .12s",
                  padding: 0,
                  zIndex: idx + 1,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translate(-50%, -50%) scale(1.5)"; e.currentTarget.style.zIndex = 99; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translate(-50%, -50%)"; e.currentTarget.style.zIndex = idx + 1; }}
              />
            );
          })}
        </div>

        {/* Zone labels */}
        <div style={{ display: "flex", fontSize: 9, color: t.muted, justifyContent: "space-between", marginTop: 2 }}>
          <span style={{ color: "#ef4444", fontWeight: 600 }}>Outside window</span>
          <span style={{ color: "#f59e0b", fontWeight: 600 }}>Approaching</span>
          <span style={{ color: "#10b981", fontWeight: 600 }}>Safe</span>
          <span style={{ color: t.muted }}>Today</span>
        </div>
      </div>

      {/* Domain summary table */}
      {domainSummary.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${t.cardBrd}` }}>
                {["Domain", "In window", "Approaching", "Outside"].map(h => (
                  <th key={h} style={{ padding: "5px 10px 5px 0", textAlign: h === "Domain" ? "left" : "center", fontSize: 9.5, fontWeight: 700, color: t.muted, letterSpacing: ".06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {domainSummary.map(row => (
                <tr
                  key={row.domain.id}
                  onClick={() => onNavToItem?.(row.domain.id)}
                  style={{ borderBottom: `1px solid ${t.cardBrd}`, cursor: "pointer", transition: "background .1s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = isLight ? "#F8FAFC" : "#1b3355"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = ""; }}
                >
                  <td style={{ padding: "7px 10px 7px 0", color: t.headText, fontWeight: 600 }}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: row.domain.color, marginRight: 6, verticalAlign: "middle" }} />
                    {row.domain.label.replace(/DOM \d+ — /, "").split("&")[0].split("/")[0].trim()}
                  </td>
                  <td style={{ textAlign: "center", padding: "7px 6px" }}>
                    <CountCell val={row.inWindow} color={row.inWindow > 0 ? "#10b981" : t.muted} />
                  </td>
                  <td style={{ textAlign: "center", padding: "7px 6px" }}>
                    <CountCell val={row.approaching} color={row.approaching > 0 ? "#f59e0b" : t.muted} />
                  </td>
                  <td style={{ textAlign: "center", padding: "7px 6px" }}>
                    <CountCell val={row.outside} color={row.outside > 0 ? "#ef4444" : t.muted} warn={row.outside > 0} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* No-date callout */}
      <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: t.muted }}>
        <Info size={11} />
        Items plotted only when an evidence date has been extracted. Upload or re-analyse documents to populate the timeline.
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, color, fontWeight: 600 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />
      {label}
    </span>
  );
}

function CountCell({ val, color, warn }) {
  if (val === 0) return <span style={{ color, fontSize: 11 }}>—</span>;
  return (
    <span style={{
      display: "inline-block",
      padding: "1px 8px",
      borderRadius: 20,
      fontWeight: 700,
      fontSize: 11,
      background: warn ? "rgba(239,68,68,.1)" : "rgba(16,185,129,.08)",
      border: `1px solid ${color}30`,
      color,
    }}>
      {val}
    </span>
  );
}
